/**
 * Stage 2 (Theresa, Oct 20 2026): the role's LinkedIn Recruiter search, built from the brief — the
 * recruiter no longer builds it in Recruiter by hand. Built by itself the first time the step opens
 * and again when the brief changed (unless the recruiter edited the string); the string stays
 * editable, and what it is made of is listed under it, so every word can be traced to the brief.
 *
 * Folded by default (Sofia, Oct 27 2026: "das sind so viele Sachen auf der Seite"): one line says
 * what the search is; a problem building it opens it.
 */
import { Button } from '@finn-auto/design-system/atoms/button';
import type { useRoleSearch } from './useRoleSearch';

export function SearchBox({ state, disabled }: { state: ReturnType<typeof useRoleSearch>; disabled: boolean }) {
  const { search, building, problem, rebuild, draft, setDraft, commit } = state;
  const value = draft ?? search?.boolean ?? '';
  const plan = search?.plan;
  const summary = building ? 'Building…' : search?.edited ? 'Your own search' : plan ? `Built from the brief · ${plan.titles.length} title${plan.titles.length === 1 ? '' : 's'}` : 'Not built yet';
  return (
    <details className="group rounded-md border border-solid border-[#E9EAEC] bg-[#F8F8F8] px-4 py-3 text-[13px]" open={Boolean(problem) || undefined} data-testid="search-box">
      <summary className="flex cursor-pointer list-none items-center gap-2 [&::-webkit-details-marker]:hidden">
        <span className="text-[#707070] transition-transform group-open:rotate-90" aria-hidden>
          ›
        </span>
        <span className="font-semibold text-[#1A1A1A]">The search for LinkedIn Recruiter</span>
        <span aria-live="polite" className="text-xs text-[#707070]">
          {summary}
        </span>
      </summary>
      <div className="mt-2 space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-xs text-[#707070]">Built from the brief, run in your Recruiter project.</span>
          <Button variant="ghost" size="sm" disabled={building || disabled} onClick={() => void rebuild()}>
            {building ? 'Building…' : search?.edited ? 'Rebuild from the brief (replaces your edit)' : 'Rebuild from the brief'}
          </Button>
        </div>
        {problem && <p className="m-0 text-[#B3261E]">{problem}</p>}
        <textarea
          aria-label="Recruiter search (Boolean)"
          className="block w-full resize-y rounded-md border border-solid border-[#E9EAEC] bg-white px-3 py-2 font-mono text-xs leading-relaxed outline-none focus:border-[#0072EA]"
          rows={4}
          value={building && !search ? 'Building the search from the brief…' : value}
          disabled={disabled || building}
          onChange={(e) => setDraft(e.target.value)}
          // Saved on leaving the field; a failed save keeps the edit on screen (see useRoleSearch.commit).
          onBlur={() => void commit()}
        />
        {plan && !search?.edited && (
          <ul className="m-0 list-none space-y-0.5 p-0 text-xs text-[#707070]">
            {plan.titles.length > 0 && <li>Titles: {plan.titles.join(' · ')}</li>}
            {plan.requirements.map((r) => (
              <li key={r.id}>
                {r.label}: {r.terms.join(' · ')}
              </li>
            ))}
            {plan.skipped.length > 0 && <li>Checked by the screening, not searched for: {plan.skipped.map((s) => s.label).join(' · ')}</li>}
            {(search?.dropped || []).length > 0 && <li className="text-[#B96716]">Left out, the search got too long: {search?.dropped?.join(' · ')}</li>}
          </ul>
        )}
        {search?.edited && <p className="m-0 text-xs text-[#707070]">Your own search — the brief does not change it.</p>}
      </div>
    </details>
  );
}
